import { useMemo } from 'react'

import { F0Select } from '@factorialco/f0-react'
import {
  TicketingResolverTeamSelectorQuery,
  TicketingResolverTeamSelectorDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

export interface Team {
  id: string
  name: string
}

interface ResolverTeamSelectorProps {
  value?: Team | null
  onSelect: (team: Team | null) => void
  label?: string
  hideLabel?: boolean
  emptyLabel?: string
  mandatory?: boolean
  disabled?: boolean
  error?: boolean
  disabledIds?: string[]
}

export const ResolverTeamSelector = ({
  value,
  onSelect,
  label,
  hideLabel = false,
  emptyLabel,
  mandatory = false,
  disabled = false,
  error = false,
  disabledIds = [],
}: ResolverTeamSelectorProps) => {
  const { data: resolverTeamsData } = useQuery<
    TicketingResolverTeamSelectorQuery,
    Record<string, never>
  >(TicketingResolverTeamSelectorDocument)

  const options = useMemo(
    () =>
      (resolverTeamsData?.ticketing?.resolverTeamsConnection?.nodes || [])
        .filter((node) => node?.teamId && node?.teamName)
        .map((node) => ({
          label: node.teamName || '',
          value: String(node.teamId || ''),
          avatar: {
            type: 'team' as const,
            name: node.teamName || '',
            src: '',
          },
          disabled: disabledIds.includes(String(node.teamId || '')),
        })),
    [resolverTeamsData, disabledIds]
  )

  const handleChange = (teamId: string) => {
    const node = resolverTeamsData?.ticketing?.resolverTeamsConnection?.nodes?.find(
      (n) => String(n.teamId) === teamId
    )
    onSelect(
      node
        ? {
            id: String(node.teamId || ''),
            name: node.teamName || '',
          }
        : null
    )
  }

  return (
    <F0Select
      label={label}
      hideLabel={hideLabel}
      value={value?.id}
      onChange={handleChange}
      options={options}
      placeholder={emptyLabel}
      size="sm"
      searchable
      showSearchBox
      disabled={disabled}
      error={error}
      required={mandatory}
    />
  )
}
